<template>
  <section>
    <v-list>
      <template v-for="(product, productIndex) in products">
        <v-list-item :key="productIndex" dense class="px-2">
          <v-list-item-avatar
            :width="imgWidth"
            :height="imgHeight"
            class="ma-0 my-1 mr-3"
            rounded
          >
            <v-img
              v-if="product.Product.PictureUrl.length > 0"
              :src="product.Product.PictureUrl"
            />
            <v-icon v-else x-large color="secondary">image</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-card
              flat
              color="grey lighten-4"
              class="px-3"
              :min-height="imgHeight"
            >
              <v-row class="my-0">
                <v-col cols="12" md="8" class="py-2">
                  <!-- Name -->
                  <v-list-item-title class="mb-1">
                    {{ product.Product.Name }}
                  </v-list-item-title>

                  <!-- Quantity -->
                  <v-list-item-subtitle>
                    {{ $translate('QUANTITY') }}:
                    {{ getDisplayQuantity(product.Product, product.Quantity) }}
                    <span v-if="isWeightedProduct(product.Product)">
                      {{ getDisplayUnit(product.Product) }}
                    </span>
                  </v-list-item-subtitle>

                  <!-- Item variations -->
                  <v-list-item-subtitle
                    v-if="product.ItemDef != null && product.ItemDef !== ''"
                  >
                    {{ product.ItemDef }}
                  </v-list-item-subtitle>

                  <!-- Options -->
                  <v-list-item-subtitle v-if="product.OptionSelectionSummary">
                    {{ product.OptionSelectionSummary }}
                  </v-list-item-subtitle>
                </v-col>

                <!-- Total price of product -->
                <v-col cols="12" md="4" class="py-2 text-right">
                  <v-list-item-title>
                    <span
                      v-if="showItemTotalRetailPrice(product)"
                      class="discount-price text-caption text--secondary mr-1"
                    >
                      {{ formatter.format(getItemTotalRetailPrice(product)) }}
                    </span>
                    {{ formatter.format(getItemTotalPrice(product)) }}
                  </v-list-item-title>
                  <span v-if="isProfUser" class="text-caption text--secondary">
                    {{ $translate('EXCL_VAT') }}
                  </span>
                </v-col>
              </v-row>
            </v-card>
          </v-list-item-content>
        </v-list-item>
      </template>

      <v-divider class="mt-2 mb-2" />

      <v-row justify="end" class="mx-0">
        <v-col cols="12" md="4">
          <!-- Subtotal price -->
          <v-row
            v-if="isProfUser"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('SUBTOTAL') }}</div>
            <div>{{ formatter.format(subtotalPrice) }}</div>
          </v-row>

          <!-- Delivery fee -->
          <v-row
            v-if="showDeliveryFee"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('DELIVERY_FEE') }}</div>
            <div>{{ formatter.format(deliveryPrice) }}</div>
          </v-row>

          <!-- Pickup fee -->
          <v-row
            v-if="hasPickupPrice"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('PICKUP_FEE') }}</div>
            <div>{{ formatter.format(pickupPrice) }}</div>
          </v-row>

          <!-- Discount -->
          <v-row
            v-if="discountPrice < 0"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('DISCOUNT') }}</div>
            <div class="red--text">{{ formatter.format(discountPrice) }}</div>
          </v-row>

          <!-- VAT -->
          <v-row
            v-if="isProfUser"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('VAT') }}</div>
            <div>{{ formatter.format(totalVATPrice) }}</div>
          </v-row>

          <!-- deposits -->
          <v-row justify="space-between" class="ma-0 text-subtitle-2" v-if="getDepositValue > 0">
            <div>{{ $translate('DEPOSIT_VALUE') }}</div>
            <div>{{ formatter.format(getDepositValue) }}</div>
          </v-row>

          <!-- Total price -->
          <v-row justify="space-between" class="ma-0 text-h6">
            <div>{{ $translate('TOTAL') }}</div>
            <div>{{ formatter.format(getTotalPrice) }}</div>
          </v-row>
        </v-col>
      </v-row>
    </v-list>
  </section>
</template>

<script>
import utility from '@/shared/utility';
import {
  getTotalRetailPriceInclVAT,
  getTotalRetailPriceExclVAT,
  getTotalPriceInclVAT,
  getTotalPriceExclVAT,
  personalDiscount,
  applyPersonalDiscount,
  getDiscount,
  isWeightedProduct,
  getDisplayUnit,
  getDisplayQuantity,
  getDeposit,
} from '@/objects/Product';
import PickupFeeJS from '@/objects/PickupFee';

export default {
  name: 'SubscriptionOverviewProducts',
  props: {
    products: Array,
  },
  data() {
    return {
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
    };
  },
  computed: {
    deliveryPickUpEvent() {
      return this.$store.getters['subscription/deliveryPickUpEvent'];
    },
    deliveryDetails() {
      return this.$store.getters['subscription/deliveryDetails'];
    },
    deliveryDetailsValid() {
      if (
        this.deliveryDetails != null &&
        this.deliveryDetails.Price != null &&
        this.deliveryDetails.ResultSet != null &&
        this.deliveryDetails.ResultSet.Price != null
      ) {
        return true;
      }
      return false;
    },
    deliveryPrice() {
      if (!this.deliveryDetailsValid) {
        return 0;
      }

      if (this.isProfUser) {
        return this.deliveryPriceExclVAT;
      }

      return this.deliveryPriceInclVAT;
    },
    deliveryPriceInclVAT() {
      if (!this.deliveryDetailsValid) {
        return 0;
      }
      return this.deliveryDetails.Price;
    },
    deliveryPriceExclVAT() {
      if (!this.deliveryDetailsValid) {
        return 0;
      }
      return (
        this.deliveryDetails.Price - this.deliveryDetails.ResultSet.VatPrice
      );
    },
    showDeliveryFee() {
      if (
        this.deliveryPickUpEvent.Type === 'Delivery' ||
        this.deliveryPickUpEvent.Type === 'Shiplee'
      ) {
        return true;
      }
      return false;
    },
    getTotalPrice() {
      let totalPrice = 0;
      totalPrice = this.totalPrice;
      if(this.getDepositValue > 0) {
        totalPrice += this.getDepositValue;
      }
      return totalPrice;
    },
    totalPrice() {
      if (this.products == null) {
        return 0;
      }

      let price = 0;

      this.products.forEach((product) => {
        let priceInclVAT = getTotalPriceInclVAT(product);
        if (priceInclVAT != null) {
          if (applyPersonalDiscount(product)) {
            priceInclVAT = utility.getDiscountPrice(
              priceInclVAT,
              personalDiscount(product)
            );
          }
          price += priceInclVAT;
        }
      });

      if (this.showDeliveryFee && this.deliveryPriceInclVAT > 0) {
        price += this.deliveryPriceInclVAT;
      }

      if (this.hasPickupPrice) {
        price += this.pickupFee.price;
      }

      return price;
    },
    imgWidth() {
      const breakPontObj = this.$vuetify.breakpoint;

      switch (breakPontObj.name) {
        case 'xs':
          return 80;
        case 'sm':
          return 100;
        case 'md':
          return 130;
        case 'lg':
        case 'xl':
        default:
          return 150;
      }
    },
    imgHeight() {
      return this.imgWidth * 0.618;
    },
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
    subtotalPrice() {
      if (this.products == null) {
        return 0;
      }

      let price = 0;

      this.products.forEach((product) => {
        let priceExclVat = getTotalRetailPriceExclVAT(product);
        if (priceExclVat == null || priceExclVat == 0) {
          priceExclVat = getTotalPriceExclVAT(product);
        }
        if (priceExclVat != null) {
          price += priceExclVat;
        }
      });

      return price;
    },
    getDepositValue() {
      let deposit = 0.0;
      this.products.forEach((product) => {
        if(product.Product.DepositValue > 0) {
          deposit += getDeposit(product);
        }
      });
      return deposit;
    },
    totalVATPrice() {
      let vatPrice = this.totalPrice - this.subtotalPrice - this.discountPrice;
      if (this.showDeliveryFee && this.deliveryPriceExclVAT > 0) {
        vatPrice -= this.deliveryPriceExclVAT;
      }
      if (this.hasPickupPrice) {
        vatPrice -= this.pickupFee.price - this.pickupFee.vatPrice;
      }
      return vatPrice;
    },
    discountPrice() {
      if (this.products == null) {
        return 0;
      }

      let price = 0;

      this.products.forEach((product) => {
        price += getDiscount(product);
      });

      return price;
    },
    pickupFee() {
      return this.$store.getters['subscription/pickupFee'];
    },
    pickupFeeValid() {
      return this.pickupFee != null && this.pickupFee instanceof PickupFeeJS;
    },
    hasPickupPrice() {
      return this.pickupFeeValid && this.pickupFee.hasPrice();
    },
    pickupPrice() {
      if (!this.hasPickupPrice) {
        return 0.0;
      }

      if (this.isProfUser) {
        return this.pickupFee.price - this.pickupFee.vatPrice;
      }
      return this.pickupFee.price;
    },
  },
  methods: {
    getItemTotalPrice(product) {
      if (applyPersonalDiscount(product)) {
        return this.getItemTotalPriceWithPersonalDiscount(product);
      }
      return this.getItemTotalPriceWithoutPersonalDiscount(product);
    },
    getItemTotalPriceWithPersonalDiscount(product) {
      let price = this.getItemTotalPriceWithoutPersonalDiscount(product);
      if (price != null && applyPersonalDiscount(product)) {
        price = utility.getDiscountPrice(price, personalDiscount(product));
      }
      return price;
    },
    getItemTotalPriceWithoutPersonalDiscount(product) {
      if (this.isProfUser) {
        return getTotalPriceExclVAT(product);
      }
      return getTotalPriceInclVAT(product);
    },
    showItemTotalRetailPrice(product) {
      return (
        this.getItemTotalRetailPrice(product) > this.getItemTotalPrice(product)
      );
    },
    getItemTotalRetailPrice(product) {
      let price = 0;
      if (this.isProfUser) {
        price = getTotalRetailPriceExclVAT(product);
        if (price == null || price == 0) {
          price = getTotalPriceExclVAT(product);
        }
      } else {
        price = getTotalRetailPriceInclVAT(product);
        if (price == null || price == 0) {
          price = getTotalPriceInclVAT(product);
        }
      }

      return price;
    },
    isWeightedProduct,
    getDisplayUnit,
    getDisplayQuantity,
  },
};
</script>
